import { useCallback, useState, useMemo, ReactNode } from 'react';

import { eachDayOfInterval, getMonth } from 'date-fns';

import { useCalendar } from '../../services/hooks';
import { Box } from '../../Box';
import { CALENDAR_MIN_DATE, CALENDAR_MAX_DATE } from '../../services/calendar';

import { Month } from './Calendar';

type Value = [Date, Date];

/**
 * Свойства компонента {@link YearCalendarInput}
 */
export interface YearCalendarInputProps {
  /**
   * Год, для которого отображается календарь.
   *
   * @defaultValue Текущий год
   */
  year?: number;

  /**
   * Массив выбранных диапазонов дат.
   */
  value: Value[];

  /**
   * Функция, вызываемая при изменении выбранных диапазонов дат.
   */
  onChange: (ranges: Value[]) => void;

  /**
   * Минимальная дата, доступная для выбора.
   *
   * @defaultValue Минимальная дата календаря
   */
  minDate?: Date;

  /**
   * Максимальная дата, доступная для выбора.
   *
   * @defaultValue Максимальная дата календаря
   */
  maxDate?: Date;

  /**
   * Функция, определяющая, должна ли дата быть отключена для выбора.
   */
  shouldDisableDate?: (date: Date, rangeStart: Date | null) => boolean;

  /**
   * Функция, возвращающая текст подсказки для указанной даты.
   */
  createTooltipText?: (date: Date) => ReactNode;

  /**
   * Функция, вызываемая при клике на выбранный диапазон дат.
   */
  onRangeClick?: (selectedRange: Value) => void;

  /**
   * Функция, вызываемая при удалении диапазона дат по индексу.
   */
  onDelete: (index: number) => void;

  /**
   * Массив праздничных дат.
   */
  holidays?: Date[];

  /**
   * Массив региональных праздничных дат.
   */
  regionalHolidays?: Date[];

  /**
   * Флаг, указывающий, является ли компонент только для чтения.
   */
  readonly?: boolean;

  /**
   * Флаг, указывающий, отключен ли компонент.
   */
  disabled?: boolean;

  /**
   * Функция, вызываемая, если нет доступных диапазонов дат.
   */
  onNoAvailableRange?: () => void;

  /**
   * Массив удаленных диапазонов дат.
   */
  deletedRanges?: Value[];

  /**
   * Массив диапазонов дат с признаком денежной компенсации.
   */
  compensationRanges?: Value[];

  /**
   * Массив заблокированных диапазонов дат (проведённые отсутствия) недоступны для выбора.
   */
  blockedRanges?: Value[];

  /**
   * Функция, вызываемая при выборе текущего интервала дат.
   */
  onSelectCurrentInterval?: (value: Value | [null, null]) => void;
}

/**
 * Компонент, который отображает календарь для заданного года, позволяя пользователям выбирать диапазоны дат.
 * Отображается в виде сетки из 12 месяцев и предоставляет функциональность для добавления, удаления и просмотра диапазонов.
 *
 * @public
 * @param year - Год, для которого отображается календарь.
 * @param value - Массив выбранных диапазонов дат.
 * @param onChange - Функция, вызываемая при изменении выбранных диапазонов дат.
 * @param minDate - Минимальная дата, доступная для выбора.
 * @param maxDate - Максимальная дата, доступная для выбора.
 * @param shouldDisableDate - Функция, определяющая, должна ли дата быть отключена для выбора.
 * @param createTooltipText - Функция, возвращающая текст подсказки для указанной даты.
 * @param onRangeClick - Функция, вызываемая при клике на выбранный диапазон дат.
 * @param onDelete - Функция, вызываемая при удалении диапазона дат по индексу.
 * @param holidays - Массив праздничных дат.
 * @param readonly - Флаг, указывающий, является ли компонент только для чтения.
 * @param disabled - Флаг, указывающий, отключен ли компонент.
 * @param onNoAvailableRange - Функция, вызываемая, если нет доступных диапазонов дат.
 * @param deletedRanges - Массив удаленных диапазонов дат.
 * @param compensationRanges - Массив диапазонов дат с признаком денежной компенсации.
 * @param blockedRanges - Массив заблокированных диапазонов дат (проведённые отсутствия).
 * @param onSelectCurrentInterval - Функция, вызываемая при выборе текущего интервала дат.
 * @param regionalHolidays - Массив региональных праздничных дат.
 * @returns Реакт-элемент, представляющий компонент YearCalendarInput.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link YearCalendarInputProps}
 */
export const YearCalendarInput = ({
  year = new Date().getFullYear(),
  value,
  onChange,
  minDate = CALENDAR_MIN_DATE,
  maxDate = CALENDAR_MAX_DATE,
  shouldDisableDate,
  createTooltipText,
  onRangeClick,
  holidays,
  regionalHolidays,
  readonly,
  disabled,
  onDelete,
  onNoAvailableRange,
  deletedRanges,
  compensationRanges,
  blockedRanges,
  onSelectCurrentInterval,
}: YearCalendarInputProps) => {
  const calendar = useCalendar();

  const [range, setRange] = useState<Value | [null, null]>([null, null]);
  const [hoverDate, setHoverDate] = useState<Date | null>(null);

  const handleReset = useCallback(() => {
    setRange([null, null]);
    onSelectCurrentInterval?.([null, null]);
  }, [onSelectCurrentInterval]);

  const days = useMemo(
    () =>
      eachDayOfInterval({
        start: new Date(`${year}-01-01`),
        end: new Date(`${year}-12-31`),
      }),
    [year],
  );

  const handleChange = useCallback(
    (nextValue: Date) => {
      if (readonly || disabled) return;

      if (
        blockedRanges?.some(
          (blocked) => nextValue >= blocked[0] && nextValue <= blocked[1],
        ) &&
        !(value && calendar.isWithinIntervals(nextValue, value))
      ) {
        return;
      }

      if (!range[0] && !range[1]) {
        setRange([nextValue, nextValue]);
        onSelectCurrentInterval?.([nextValue, nextValue]);

        if (
          onNoAvailableRange &&
          shouldDisableDate &&
          days.every((day) => shouldDisableDate(day, nextValue))
        ) {
          onNoAvailableRange();
        }
      } else if (
        range[0] &&
        range[1] &&
        calendar.isSameDay(range[0], range[1])
      ) {
        let newValue: Value;

        if (nextValue >= range[0]) {
          newValue = [range[0], nextValue];
        } else {
          newValue = [nextValue, range[0]];
        }

        setRange([null, null]);
        onSelectCurrentInterval?.([null, null]);
        onChange([...value, newValue]);
      }
    },
    [
      readonly,
      disabled,
      blockedRanges,
      range,
      calendar,
      onSelectCurrentInterval,
      onNoAvailableRange,
      shouldDisableDate,
      days,
      onChange,
      value,
    ],
  );

  const handleDelete = useCallback(
    (date: Date) => {
      onDelete(
        value.findIndex(
          (selectedRange) =>
            date >= selectedRange[0] && date <= selectedRange[1],
        ),
      );
    },
    [onDelete, value],
  );

  return (
    <Box
      mb="8"
      display="flex"
      justifyContent="center"
      flexWrap="wrap"
      testId="year-calendar-input"
    >
      {new Array(12).fill(null).map((_, index) => (
        <Month
          year={year}
          month={index}
          key={getMonth(index)}
          value={value}
          onChange={handleChange}
          minDate={minDate}
          maxDate={maxDate}
          createTooltipText={createTooltipText}
          shouldDisableDate={shouldDisableDate}
          holidays={holidays}
          regionalHolidays={regionalHolidays}
          onRangeClick={onRangeClick}
          hoverDate={hoverDate}
          setHoverDate={setHoverDate}
          range={range}
          readonly={readonly}
          disabled={disabled}
          onDelete={handleDelete}
          clearable={!!range[0] && range[0].getMonth() === index}
          onClear={handleReset}
          deletedRanges={deletedRanges}
          compensationRanges={compensationRanges}
          blockedRanges={blockedRanges}
          testId={`year-calendar-input-month-${index}`}
        />
      ))}
    </Box>
  );
};
